/** ******************************************************* {COPYRIGHT-TOP} ***
 * Licensed Materials - Property of IBM
 *
 * (C) Copyright IBM Corporation 2016, 2025
 *
 * All Rights Reserved.
 * US Government Users Restricted Rights - Use, duplication or disclosure
 * restricted by GSA ADP Schedule Contract with IBM Corp.
 ********************************************************** {COPYRIGHT-END} * */
/* eslint-disable react/no-unstable-nested-components */
import React from 'react';
import PropTypes from 'prop-types';
import { Loading } from '@carbon/react';
import { JSONTree } from 'react-json-tree';
import DOMPurify from 'dompurify';
import MarkdownIt from 'markdown-it';

const md = new MarkdownIt();

function Load() {
  return <Loading small withOverlay={false} data-testid="json-loading-icon" />;
}


// Label renderer function extracted outside component
function labelRenderer(keyPath) {
  const [label] = keyPath;
  if (keyPath.length === 1 && label === 'root') {
    return <span>{}</span>;
  }
  return <span>{typeof label === 'string' ? `"${label}"` : String(label)}<span className='jsonDataKeyColon'>:</span></span>;
}

function ViewableJSONDisplay({ content, jsonName = null }) {
  // Renders markdown only for "description" keys
  const valueRenderer = (raw, value, keyPath) => {
    const path = Array.isArray(keyPath) ? keyPath : [keyPath];
    const isDescription = path[path.length - 1] === 'description';

    if (isDescription && typeof value === 'string') {
      const markdownInput = value;
      const renderedHtml = md.render(markdownInput);
      const safeHtml = DOMPurify.sanitize(renderedHtml);
      return <div style={{ padding: '0.25rem 0' }} className="markdown-description" dangerouslySetInnerHTML={{ __html: safeHtml }} />;
    }

    return <span>{typeof value === 'string' ? `"${value}"` : String(value)}</span>;
  };

  const explorerTheme = {
    base00: '#f4f4f4', // background
    base01: '#f4f4f4',
    base02: '#e0e0e0',
    base03: '#6f6f6f', // helper text (gray)
    base04: '#525252',
    base05: '#161616',
    base06: '#161616',
    base07: '#161616', // TEXT_COLOR
    base08: '#da1e28', // NULL_COLOR, UNDEFINED_COLOR, FUNCTION_COLOR, SYMBOL_COLOR
    base09: '#4646e6', // numbers / booleans (blue-purple)
    base0A: '#0f62fe',
    base0B: '#008000', // STRING_COLOR, DATE_COLOR, ITEM_STRING_COLOR (green)
    base0C: '#0043ce',
    base0D: '#660066', // keys (purple)
    base0E: '#6f42c1',
    base0F: '#8a3ffc',
  };

  return (
    <>
      {!content && <Load />}
      {content && (
        <div className='jsonDataTreeDisplay' data-testid="json-data">
          {jsonName && (
            <span data-testid="json-name" style={{ display: 'none' }}>
              {jsonName}
            </span>
          )}
          <JSONTree
            data={content}
            theme={explorerTheme}
            valueRenderer={valueRenderer}
            hideRoot={false}
            labelRenderer={labelRenderer}
            shouldExpandNodeInitially={() => true}
          />
        </div>
      )}
    </>
  );
}

ViewableJSONDisplay.propTypes = {
  content: PropTypes.oneOfType([PropTypes.object, PropTypes.array]), // JSON Content to be displayed
  jsonName: PropTypes.string // Name to be displayed at top of JSON Viewer
};

export default ViewableJSONDisplay;
